/**
 * Nuovo allenamento (V11) e modifica dalla scheda (V13), nello stesso modale.
 *
 * Il nuovo si apre dagli allenamenti (V10), anche dal giorno scelto nel
 * calendario, che arriva con giorno e ora gia compilati ma modificabili, e
 * dal cruscotto; salvato, porta alla scheda. La modifica si apre dalla
 * scheda e, salvata, la ricarica.
 *
 * Con presenze, voti o Registro seduta già registrati il giorno non si cambia più, solo l'ora:
 * il server lo rifiuta e il form lo dice prima, cosi non si scopre al
 * salvataggio.
 */

import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allenamentoSchema, dataLocale } from '@calcio/shared';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { CampoData } from '../comuni/CampoData.js';
import type { GiornoProposto } from '../comuni/calendario.js';
import { CampoSuggerito } from '../comuni/CampoSuggerito.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { dataBreve, erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { daInputDataOra, perInputDataOra } from '../partite/partita.js';
import {
  giornoBloccato,
  type DettaglioAllenamento,
  type OpzioniAllenamento,
} from './allenamenti.js';

export function DialogAllenamento({
  ss,
  aperto,
  onChiudi,
  esistente,
  giorno,
  onSalvato,
}: {
  ss: string;
  aperto: boolean;
  onChiudi: () => void;
  /** L'allenamento da modificare; senza, e un nuovo. */
  esistente?: DettaglioAllenamento;
  giorno?: GiornoProposto;
  /** Dopo la modifica; il nuovo porta invece alla sua scheda. */
  onSalvato?: () => Promise<void> | void;
}) {
  return (
    <Dialog open={aperto} onOpenChange={(a) => !a && onChiudi()}>
      <DialogContent>
        {aperto && (
          <Caricamento
            ss={ss}
            esistente={esistente}
            giorno={giorno}
            onChiudi={onChiudi}
            onSalvato={onSalvato}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Caricamento({
  ss,
  esistente,
  giorno,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  esistente?: DettaglioAllenamento;
  giorno?: GiornoProposto;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const opzioni = useDati<OpzioniAllenamento>(`/squadre-stagione/${ss}/allenamenti/opzioni`);
  if (!opzioni.dati) {
    return (
      <>
        <Intestazione esistente={esistente} />
        <StatoCaricamento errore={opzioni.errore} />
      </>
    );
  }
  return (
    <Form
      ss={ss}
      opzioni={opzioni.dati}
      esistente={esistente}
      giorno={giorno}
      onChiudi={onChiudi}
      onSalvato={onSalvato}
    />
  );
}

function Intestazione({ esistente }: { esistente?: DettaglioAllenamento }) {
  return (
    <DialogHeader>
      <DialogTitle>{esistente ? 'Modifica allenamento' : 'Nuovo allenamento'}</DialogTitle>
      <DialogDescription>
        {esistente
          ? 'Data, ora, luogo e note dell’allenamento.'
          : 'Un solo allenamento; per un periodo intero usa la creazione in blocco.'}
      </DialogDescription>
    </DialogHeader>
  );
}

function Form({
  ss,
  opzioni,
  esistente,
  giorno,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  opzioni: OpzioniAllenamento;
  esistente?: DettaglioAllenamento;
  giorno?: GiornoProposto;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const a = esistente?.allenamento;
  // Giorno e ora in due campi, come "2026-09-23" e "18:00", nell'ora italiana.
  const [data, setData] = useState(() =>
    a ? perInputDataOra(a.dataOra).slice(0, 10) : (giorno?.data ?? ''),
  );
  const [ora, setOra] = useState(() =>
    a ? perInputDataOra(a.dataOra).slice(11) : (giorno?.ora ?? ''),
  );
  const [luogo, setLuogo] = useState(a?.luogo ?? opzioni.luoghi[0] ?? '');
  const [note, setNote] = useState(a?.note ?? '');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // I voti arrivano solo a chi li legge: per il Dirigente contano le presenze,
  // e il server rifiuta comunque se ci sono voti.
  const giornoFisso = Boolean(esistente && giornoBloccato(esistente));
  const { dataInizio, dataFine } = opzioni.stagione;

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = allenamentoSchema.safeParse({
      dataOra: daInputDataOra(`${data}T${ora}`),
      luogo,
      note,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    if (giornoFisso && a && dataLocale(esito.data.dataOra) !== dataLocale(a.dataOra)) {
      return setErrori({
        dataOra:
          "Con presenze, voti o Registro seduta già registrati si cambia l'ora, non il giorno.",
      });
    }
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      const { id } = await chiama<{ id: string }>(
        a ? `/allenamenti/${a.id}` : `/squadre-stagione/${ss}/allenamenti`,
        { metodo: a ? 'PUT' : 'POST', corpo: esito.data },
      );
      toast.success(a ? 'Allenamento salvato.' : 'Allenamento creato.');
      if (a) {
        await onSalvato?.();
        onChiudi();
      } else navigate(`/sq/${ss}/allenamenti/${id}`);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <Intestazione esistente={esistente} />

      <FieldGroup>
        <Field data-invalid={Boolean(errori.dataOra)}>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 flex flex-col gap-3">
              <FieldLabel htmlFor="allenamento-data">Giorno</FieldLabel>
              <CampoData
                id="allenamento-data"
                valore={data}
                onCambia={setData}
                min={dataInizio}
                max={dataFine}
                invalido={Boolean(errori.dataOra)}
              />
            </div>
            <div className="flex flex-col gap-3">
              <FieldLabel htmlFor="allenamento-ora">Ora</FieldLabel>
              <Input
                id="allenamento-ora"
                type="time"
                value={ora}
                aria-invalid={Boolean(errori.dataOra)}
                onChange={(e) => setOra(e.target.value)}
              />
            </div>
          </div>
          {errori.dataOra ? (
            <FieldError>{errori.dataOra}</FieldError>
          ) : (
            <FieldDescription>
              {giornoFisso
                ? "Ci sono già presenze, voti o Registro seduta: puoi cambiare l'ora, non il giorno."
                : `Nella stagione, dal ${dataBreve(dataInizio)} al ${dataBreve(dataFine)}.`}
            </FieldDescription>
          )}
        </Field>

        <Field data-invalid={Boolean(errori.luogo)}>
          <FieldLabel htmlFor="allenamento-luogo">Luogo</FieldLabel>
          <CampoSuggerito
            id="allenamento-luogo"
            valore={luogo}
            opzioni={opzioni.luoghi}
            onCambia={setLuogo}
            segnaposto="Scegli o scrivi il luogo"
            nuovo={(t) => `Usa «${t}»`}
            invalido={Boolean(errori.luogo)}
          />
          <FieldError>{errori.luogo}</FieldError>
        </Field>

        <Field data-invalid={Boolean(errori.note)}>
          <FieldLabel htmlFor="allenamento-note">Note</FieldLabel>
          <Textarea
            id="allenamento-note"
            value={note}
            aria-invalid={Boolean(errori.note)}
            onChange={(e) => setNote(e.target.value)}
          />
          <FieldError>{errori.note}</FieldError>
        </Field>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Salva
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
